import { ActionType } from 'typesafe-actions';
import { all, put, select, takeEvery } from 'redux-saga/effects';

import {
  passportGroupApplyAction,
  passportGroupApplyByKnoAction,
  passportGroupCreateAction,
  passportGroupDeleteAction,
  passportGroupExcludePassportsAction,
  passportGroupExportAction,
  passportGroupGetAction,
  passportGroupGetNextPageAction,
  passportGroupGetPageAction,
  passportGroupPassportApplyByKno,
  passportGroupPassportGetNextPageAction,
  passportGroupPassportGetPageAction,
  passportGroupPassportSetCurrentPageAction,
  passportGroupPassportSetListAction,
  passportGroupPassportSetTotalCountAction,
  passportGroupSetAction,
  passportGroupSetApplySuccessAction,
  passportGroupSetCreateSuccessAction,
  passportGroupSetIsDeletedAction,
  passportGroupSetIsExistsGroupsForCancellationAction,
  passportGroupSetListAction,
  passportGroupSetListCurrentPageAction,
  passportGroupSetListTotalCountAction,
  passportGroupsGetRiskIndicatorsSelectOptions,
} from '../actions/passportGroupActions';
import { PassportGroupActionsEnum } from '../enums/PassportGroupActionsEnum';
import { sagaWrapper } from '../../../CorePackage/MainModule/common/sagaWrapper';
import {
  applyPassportByKno,
  applyPassportGroup,
  applyPassportGroupByKno,
  createPassportGroup,
  deletePassportGroup,
  excludePassports,
  exportPassportGroup,
  fetchIsExistsGroupsForCancellation,
  fetchPassportGroup,
  fetchPassportGroupPage,
  fetchPassportGroupPassportPage,
} from '../../../APIPackage/PassportGroupsModule/services/passportGroupsApi';
import { IPassportGroupPage } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupPage';
import { IPassportGroupShort } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupShort';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
} from '../../../CorePackage/MainModule/constants/dateFormats';
import {
  passportGroupListCurrentPageSelector,
  passportGroupListSelector,
  passportGroupPassportListCurrentPageSelector,
  passportGroupPassportListSelector,
} from '../selectors/passportGroupSelector';
import { IPassportGroupPassportPage } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupPassportPage';
import { IPassportGroupPassport } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupPassport';
import { IPassportGroup } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroup';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { fetchRiskIndicatorsDictionaryByQuery } from '../../../APIPackage/DictionariesModule/services/dictionariesApi';
import { IDictionaryValues } from '../../../APIPackage/DictionariesModule/interfaces/IDictionaryValues';
import { IRiskIndicatory } from '../../../APIPackage/DictionariesModule/interfaces/IRiskIndicatory';
import { createDictionaryRiskIndicatorySelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionaryRiskIndicatorySelectOption';
import { INTERNAL_SERVER_ERROR } from '../../../CorePackage/MainModule/constants/notices';
import { watchPassportGroupInitFormSaga } from './passportGroupInitFormSaga';
import { PassportGroupPassportTypeEnum } from '../../PassportGroupFormModule/enums/PassportGroupPassportTypeEnum';
import { decisionGet, decisionGetHistoryChanges } from '../../../DecisionPackage/MainModule/actions/decisionActions';
import { IResponseError } from '../../../APIPackage/MainModule/interfaces/IResponseError';
import { knmGet, knmGetHistoryChanges } from '../../../KnmPackage/MainModule/actions/knmActions';
import { addNoticeImportNotification } from '../../../CorePackage/NotificationModule/common/addNoticeImportNotification';
import { IExport } from '../../../APIPackage/ImportExportModule/interfaces/IExport';

const PAGE_SIZE = 50;

const convertPassportGroupList = (list: IPassportGroupShort[]): IPassportGroupShort[] => {
  return list.map(item => ({
    ...item,
    createdAt: formatDate({
      date: item.createdAt,
      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
      parseFormat: DATE_FORMAT_ISO_FNS,
    }),
  }));
};

const noticeInternalError = () =>
  addNoticeAction({
    renderContent: INTERNAL_SERVER_ERROR,
    noticeType: NoticeTypesEnum.ERROR,
    messageType: MessageTypesEnum.PASSPORT_GROUP,
  });

export function* watchPassportGroupSaga() {
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_CREATE, passportGroupCreateSaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_GET_PAGE, passportGroupGetPageSaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_GET_NEXT_PAGE, passportGroupGetNextPageSaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_PASSPORT_GET_PAGE, passportGroupPassportGetPageSaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_PASSPORT_GET_NEXT_PAGE, passportGroupPassportGetNextPageSaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_GET, passportGroupGetSaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_APPLY, passportGroupApplySaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_APPLY_BY_KNO, passportGroupApplyByKnoSaga);
  yield takeEvery(
    PassportGroupActionsEnum.PASSPORT_GROUP_GET_RISK_INDICATORS_SELECT_OPTIONS,
    passportGroupGetRiskIndicatorsSelectOptionsSaga,
  );
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_PASSPORT_APPLY_BY_KNO, passportGroupPassportApplyByKnoSaga);
  yield takeEvery(
    PassportGroupActionsEnum.PASSPORT_GROUP_GET_IS_EXISTS_GROUPS_FOR_CANCELLATION,
    getIsExistsGroupsForCancellationSaga,
  );
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_EXPORT, passportGroupExportSaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_DELETE, passportGroupDeleteSaga);
  yield takeEvery(PassportGroupActionsEnum.PASSPORT_GROUP_EXCLUDE_PASSPORTS, passportGroupExcludePassportsSaga);
  yield all([watchPassportGroupInitFormSaga()]);
}

function* passportGroupCreateSaga(action: ActionType<typeof passportGroupCreateAction>) {
  function* funcTry() {
    yield createPassportGroup(action.payload.data);
    yield put(passportGroupSetCreateSuccessAction(true));
  }

  yield sagaWrapper({ funcTry });
}

function* passportGroupGetPageSaga(action: ActionType<typeof passportGroupGetPageAction>) {
  function* funcTry() {
    const { type, reasonId } = action.payload;
    const data: IPassportGroupPage = yield fetchPassportGroupPage(0, PAGE_SIZE, type, reasonId);

    yield put(passportGroupSetListAction(convertPassportGroupList(data.list)));
    yield put(passportGroupSetListTotalCountAction(data.totalCount));
    yield put(passportGroupSetListCurrentPageAction(data.page));
  }

  function* funcCatch() {
    yield put(passportGroupSetListAction(convertPassportGroupList([])));
    yield put(passportGroupSetListTotalCountAction(0));
    yield put(passportGroupSetListCurrentPageAction(0));
    yield put(noticeInternalError());
  }

  yield sagaWrapper({ funcTry, funcCatch });
}

function* passportGroupGetNextPageSaga(action: ActionType<typeof passportGroupGetNextPageAction>) {
  function* funcTry() {
    const { type, reasonId } = action.payload;
    const currentList: IPassportGroupShort[] = yield select(passportGroupListSelector);
    const currentPage: number = yield select(passportGroupListCurrentPageSelector);
    const data: IPassportGroupPage = yield fetchPassportGroupPage(currentPage + 1, PAGE_SIZE, type, reasonId);
    const result = [...currentList, ...convertPassportGroupList(data.list)];

    yield put(passportGroupSetListAction(result));
    yield put(passportGroupSetListTotalCountAction(data.totalCount));
    yield put(passportGroupSetListCurrentPageAction(data.page));
  }

  function* funcCatch() {
    yield put(noticeInternalError());
  }

  yield sagaWrapper({ funcTry, funcCatch });
}

function* passportGroupPassportGetPageSaga(action: ActionType<typeof passportGroupPassportGetPageAction>) {
  function* funcTry() {
    const data: IPassportGroupPassportPage = yield fetchPassportGroupPassportPage(action.payload.groupId, 0, PAGE_SIZE);

    yield put(passportGroupPassportSetListAction(data.list));
    yield put(passportGroupPassportSetTotalCountAction(data.totalCount));
    yield put(passportGroupPassportSetCurrentPageAction(data.page));
  }

  function* funcCatch() {
    yield put(passportGroupPassportSetListAction([]));
    yield put(passportGroupPassportSetTotalCountAction(0));
    yield put(passportGroupPassportSetCurrentPageAction(0));
  }

  yield sagaWrapper({ funcTry, funcCatch });
}

function* passportGroupPassportGetNextPageSaga(action: ActionType<typeof passportGroupPassportGetNextPageAction>) {
  function* funcTry() {
    const currentList: IPassportGroupPassport[] = yield select(passportGroupPassportListSelector);
    const currentPage: number = yield select(passportGroupPassportListCurrentPageSelector);
    const data: IPassportGroupPassportPage = yield fetchPassportGroupPassportPage(
      action.payload.groupId,
      currentPage + 1,
      PAGE_SIZE,
    );
    const result = [...currentList, ...data.list];

    yield put(passportGroupPassportSetListAction(result));
    yield put(passportGroupPassportSetTotalCountAction(data.totalCount));
    yield put(passportGroupPassportSetCurrentPageAction(data.page));
  }

  yield sagaWrapper({ funcTry });
}

function* passportGroupGetSaga(action: ActionType<typeof passportGroupGetAction>) {
  function* funcTry() {
    const data: IPassportGroup = yield fetchPassportGroup(action.payload.id);

    yield put(passportGroupSetAction(data));
  }

  function* funcCatch() {
    yield put(passportGroupSetAction(undefined));
  }

  yield sagaWrapper({ funcTry, funcCatch });
}

function* passportGroupApplySaga(action: ActionType<typeof passportGroupApplyAction>) {
  function* funcTry() {
    yield applyPassportGroup(action.payload.groupId, action.payload.data);
    yield put(passportGroupSetApplySuccessAction(true));
    yield put(passportGroupGetAction(action.payload.groupId));
  }

  yield sagaWrapper({ funcTry });
}

function* passportGroupApplyByKnoSaga(action: ActionType<typeof passportGroupApplyByKnoAction>) {
  function* funcTry() {
    yield applyPassportGroupByKno(action.payload.groupId, action.payload.data);
    yield put(passportGroupSetApplySuccessAction(true));
    yield put(
      addNoticeAction({
        renderContent: 'Запущен фоновый процесс отмены мероприятий группы',
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.PASSPORT_GROUP,
      }),
    );
    yield put(passportGroupGetAction(action.payload.groupId));
  }

  yield sagaWrapper({ funcTry });
}

function* passportGroupGetRiskIndicatorsSelectOptionsSaga(
  action: ActionType<typeof passportGroupsGetRiskIndicatorsSelectOptions>,
) {
  function* funcTry() {
    const { query, callback } = action.payload;
    const result: IDictionaryValues<IRiskIndicatory> = yield fetchRiskIndicatorsDictionaryByQuery(query);
    const options = result.values
      .map(item => createDictionaryRiskIndicatorySelectOption(result.idDictionary, item))
      .map(item => ({ ...item, label: item.shortTitle || item.title }));

    callback(options);
  }

  yield sagaWrapper({ funcTry });
}

function* passportGroupPassportApplyByKnoSaga(action: ActionType<typeof passportGroupPassportApplyByKno>) {
  function* funcTry() {
    const { passportId, passportType, decision } = action.payload;

    yield applyPassportByKno(passportId, passportType, decision);

    switch (passportType) {
      case PassportGroupPassportTypeEnum.ERKNM:
      case PassportGroupPassportTypeEnum.PM:
        yield put(knmGet());
        yield put(knmGetHistoryChanges());
        break;
      case PassportGroupPassportTypeEnum.DECISION_PASSPORT:
        yield put(decisionGet(passportId));
        yield put(decisionGetHistoryChanges());
        break;
      default:
        break;
    }
  }

  function* funcCatch(currentError: IResponseError) {
    yield put(
      addNoticeAction({
        renderContent: currentError.message || currentError.text,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.PASSPORT_GROUP,
      }),
    );
  }

  yield sagaWrapper({ funcTry, funcCatch });
}

function* getIsExistsGroupsForCancellationSaga() {
  function* funcTry() {
    const isExist: boolean = yield fetchIsExistsGroupsForCancellation();

    yield put(passportGroupSetIsExistsGroupsForCancellationAction(isExist));
  }

  yield sagaWrapper({ funcTry });
}

function* passportGroupExportSaga(action: ActionType<typeof passportGroupExportAction>) {
  function* funcTry() {
    const { groupId, format } = action.payload;

    const data: IExport = yield exportPassportGroup(groupId, format);

    if (data.id) {
      yield put(addNoticeImportNotification(data.id));
    }
  }

  yield sagaWrapper({ funcTry });
}

function* passportGroupDeleteSaga(action: ActionType<typeof passportGroupDeleteAction>) {
  function* funcTry() {
    yield deletePassportGroup(action.payload.groupId);
    yield put(passportGroupSetIsDeletedAction(true));
  }

  yield sagaWrapper({ funcTry });
}

function* passportGroupExcludePassportsSaga(action: ActionType<typeof passportGroupExcludePassportsAction>) {
  function* funcTry() {
    const { groupId, passportIds } = action.payload;
    const isGroupDeleted: boolean = yield excludePassports(groupId, passportIds);

    if (isGroupDeleted) {
      yield put(passportGroupSetIsDeletedAction(true));
    } else {
      yield put(passportGroupGetAction(groupId));
      yield put(passportGroupPassportGetPageAction(groupId));
    }
  }

  function* funcCatch() {
    yield put(noticeInternalError());
  }

  yield sagaWrapper({ funcTry, funcCatch });
}
